import { OfferStatusUpdate } from "./modals/offer-status-update";
import { OfferT } from "../../../domain";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Modal,
  RepeatIcon,
  TriangleIcon,
  useDisclosure,
} from "../../../../../shared/infrastructure";

type SetStatusProps = {
  open: () => void;
  statusColor: string;
};

const SetStatus = ({
  open,
  statusColor,
}: SetStatusProps): React.ReactElement => {
  const [t] = useTranslation("global");

  return (
    <React.Fragment>
      <span className={`h-[10px] w-[10px] rounded-full ${statusColor}`} />
      <button
        onClick={open}
        className="grid grid-flow-col items-center gap-[8px]"
      >
        {t<string>("context.private.components.offer-card.set-status")}
        <TriangleIcon />
      </button>
    </React.Fragment>
  );
};

type CarrierBidProps = {
  open: () => void;
  carrier: string;
  value: number | null;
};

const CarrierBid = ({
  open,
  carrier,
  value = 0,
}: CarrierBidProps): React.ReactElement => {
  return (
    <div className="leading-[normal]; flex gap-[12px] text-xs font-medium not-italic text-black">
      {carrier} Bid {value} USD{" "}
      <button
        className="leading-[normal]; flex gap-[8px] text-xs font-normal not-italic text-[#147EFB]"
        onClick={open}
      >
        Answer <RepeatIcon />
      </button>
    </div>
  );
};
type StatusProps = {
  name: string;
  status: string;
  value: number;
};
const Status = ({ name, status, value }: StatusProps): React.ReactElement => {
  return (
    <div
      style={{ color: status === "Approved" ? "#4CAF50" : "#F44336" }}
      className={`text-xs font-medium not-italic leading-[normal]`}
    >
      {`${name} ${status} ${value} USD`}
    </div>
  );
};

type OfferCardStatusProps = {
  data: OfferT;
  statusColor: string;
  section?: string;
};

export const OfferCardStatus = ({
  data,
  statusColor,
  section = "queue",
}: OfferCardStatusProps): React.ReactElement => {
  const { isOpen, open, close } = useDisclosure();

  const { last_capacity_negotiation_move: lastMove } = data;

  const renderSetStatus = (): React.ReactNode => (
    <SetStatus open={open} statusColor={statusColor} />
  );
  const renderCarrierBid = (): React.ReactNode => (
    <CarrierBid
      open={open}
      carrier={data.carrier.name}
      value={data.counter_offer}
    />
  );

  const renderStatus = (): React.ReactNode | null =>
    lastMove ? (
      <Status
        name={
          lastMove.platform === "CAPACITY"
            ? "Carrier"
            : lastMove.approver_user?.name ?? "--"
        }
        status={
          lastMove.status === "confirmed" || lastMove.status === "approved"
            ? "Approved"
            : "Rejected"
        }
        value={lastMove.rate}
      />
    ) : null;

  const [t] = useTranslation("global");

  return (
    <React.Fragment>
      {lastMove &&
      !["approved", "confirmed", "rejected", "bid", "pending"].includes(
        lastMove?.status
      ) ? (
        <React.Fragment>
          {t<string>("context.private.components.offer-card.status")}:{" "}
          <span className="capitalize">{data.status}</span>
        </React.Fragment>
      ) : null}

      {lastMove === null ? (
        section === "queue" ? (
          renderSetStatus()
        ) : (
          <React.Fragment>
            {t<string>("context.private.components.offer-card.status")}:{" "}
            <span className="capitalize">{data.status}</span>
          </React.Fragment>
        )
      ) : null}

      {lastMove && lastMove.status === "pending" ? (
        section === "queue" ? (
          renderSetStatus()
        ) : (
          <React.Fragment>
            {t<string>("context.private.components.offer-card.status")}:{" "}
            <span className="capitalize">{lastMove.status}</span>
          </React.Fragment>
        )
      ) : null}

      {lastMove && lastMove.platform === "CAPACITY" && lastMove.status === "bid"
        ? renderCarrierBid()
        : null}
      {!(
        lastMove &&
        !["approved", "confirmed", "rejected", "bid", "pending"].includes(
          lastMove.status
        )
      ) &&
      !(lastMove && lastMove.status === "pending") &&
      !(
        lastMove &&
        lastMove.platform === "CAPACITY" &&
        lastMove.status === "bid"
      )
        ? renderStatus()
        : null}
      <Modal
        open={isOpen}
        onClose={close}
        content={<OfferStatusUpdate data={data} close={close} />}
        classNames={{
          content: "overflow-hidden p-[20px] min-w-[332px] min-h-[214px]",
          paper: "rounded-[5px]",
        }}
      />
    </React.Fragment>
  );
};
